"use client";

import { localizedPathWithExpertiseCityFallback } from "@/lib/i18n/expertiseCityLocaleFallbackPath";
import { localeInfos, type Locale } from "@/lib/i18n/locales";
import { stripLocaleFromPathname } from "@/lib/i18n/navigation";
import { Language } from "@mui/icons-material";
import {
  Box,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { usePathname, useRouter } from "next/navigation";
import { useId, useState } from "react";
import { useLocale } from "./LocaleProvider";

type SLLanguageSwitcherProps = {
  variant?: "icon" | "fullWidth";
};

export function SLLanguageSwitcher({
  variant = "icon",
}: SLLanguageSwitcherProps) {
  const { locale, setLocale } = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePick = (code: Locale) => {
    const rest = stripLocaleFromPathname(pathname);
    const target = localizedPathWithExpertiseCityFallback(rest, code);
    router.push(target);
    setLocale(code);
    handleClose();
  };

  if (variant === "fullWidth") {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <Typography
          component="p"
          variant="overline"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Sprache / Language
        </Typography>
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            width: "100%",
          }}
        >
          {localeInfos.map((info) => (
            <Box component="li" key={info.code} sx={{ width: "100%" }}>
              <MenuItem
                selected={info.code === locale}
                onClick={() => handlePick(info.code)}
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  justifyContent: "flex-start",
                }}
              >
                <ListItemText
                  primary={info.label}
                  secondary={info.shortLabel}
                />
              </MenuItem>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <IconButton
        id={`${menuId}-lang-trigger`}
        aria-label="Sprache wählen"
        aria-controls={open ? `${menuId}-lang-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        size="large"
        sx={{
          color: theme.palette.text.primary,
          alignSelf: "center",
          flexShrink: 0,
        }}
      >
        <Language />
      </IconButton>
      <Menu
        id={`${menuId}-lang-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.palette.background.default,
              minWidth: 200,
            },
          },
        }}
      >
        {localeInfos.map((info) => (
          <MenuItem
            key={info.code}
            selected={info.code === locale}
            onClick={() => handlePick(info.code)}
          >
            <ListItemText primary={info.label} secondary={info.shortLabel} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
